import React, { useState, useEffect, useRef } from 'react';
import { 
    XMarkIcon,
    CreditCardIcon,
    UserIcon,
    DocumentTextIcon,
    FolderIcon,
    HeartIcon,
    SparklesIcon,
    PlusIcon,
    KeyIcon,
    CalendarIcon,
    MapPinIcon,
    EyeIcon,
    EyeSlashIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon, ShieldCheckIcon } from '@heroicons/react/24/solid';
import { CardDetail } from '../../types/cardDetails';
import CardDetailsService from '../../services/cardDetails.service';
import { toast } from 'react-hot-toast';
import Button from '../common/Button';
import { useCategories } from '../../contexts/CategoryContext';
import { decryptKeyData } from '../../utils/encryption';
import { secureStore } from '../../utils/secureStore';
import { useAppSelector } from '../../hooks/useRedux';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { MultiSelect } from 'react-multi-select-component';

interface Option {
    label: string;
    value: string;
    disabled?: boolean;
    isShared?: boolean;
    sharedKey?: string;
}
interface FormData {
    name: string;
    category_id: string;
    card_type: string;
    encrypted_card_holder_name: string;
    encrypted_card_number: string;
    encrypted_expiration_date: string;
    encrypted_cvv: string;
    encrypted_pin: string;
    encrypted_postal_code: string;
    encrypted_notes: string;
    favourite: boolean;
    shared_key: string;
    category_ids: string[];
    remove_category_ids: string[];
}
interface CardDetailsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    card?: CardDetail;
    isSharedCard?: boolean;
    permissionLevel?: string;
    sharedKey?: string;
    onSuccessfulUpdate?: () => Promise<void> | void;
    categoryId?: string;
    isAdminView?: boolean;   
}

const CardDetailsModal: React.FC<CardDetailsModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    card,
    isSharedCard = false,
    permissionLevel = 'read',
    sharedKey,
    onSuccessfulUpdate,
    categoryId,
    isAdminView = false
}) => {
    const initialCategoriesRef = useRef<string[]>([]);
    
    const [formData, setFormData] = useState<FormData>({
        name: '',
        category_id: categoryId || '',
        card_type: '',
        encrypted_card_holder_name: '',
        encrypted_card_number: '',
        encrypted_expiration_date: '',
        encrypted_cvv: '',
        encrypted_pin: '',
        encrypted_postal_code: '',
        encrypted_notes: '',
        favourite: false,
        shared_key: '',
        category_ids: [],
        remove_category_ids: [],
    });

    const [loading, setLoading] = useState(false);
    const { categories, refreshCategories, createCategory } = useCategories();
    const [isCreatingCategory, setIsCreatingCategory] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [creatingCategoryLoading, setCreatingCategoryLoading] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<any>(null);
    const { activeWorkspace } = useWorkspace();
    const { team_id, role_id } = useAppSelector(state => state.auth);

    const categoryOptions: Option[] = categories.filter(cat => !(cat.permission_level === 'read')).map(cat => ({
        label: cat.name,
        value: cat.id,
        isShared: cat.is_shared,
        sharedKey: cat.shared_key
    }));

    // Initialize selected options from existing data
    const initializeSelectedOptions = (): Option[] => {
        if (card?.categories && card.categories.length > 0) {
            const validCategories = card.categories.filter(cardCategory => 
                categories.some(userCategory => userCategory.id === cardCategory.id)
            );
            return validCategories.map(cat => ({
                label: cat.name,
                value: cat.id,
                isShared: cat.is_shared,
                sharedKey: cat.shared_key
            }));
        } else if (card?.category_id) {
            const category = categories.find(c => c.id === card.category_id);
            if (category) {
                return [{
                    label: category.name,
                    value: category.id,
                    isShared: category.is_shared,
                    sharedKey: category.shared_key
                }];
            }
        } else if (categoryId) {
            const category = categories.find(c => c.id === categoryId);
            if (category) {
                return [{
                    label: category.name,
                    value: category.id,
                    isShared: category.is_shared,
                    sharedKey: category.shared_key
                }];
            }
        }
        return [];
    };

    const [selectedOptions, setSelectedOptions] = useState<Option[]>(initializeSelectedOptions());
    const [showCvv, setShowCvv] = useState(false);
    const [showCardPin, setShowCardPin] = useState(false);

    // Initialize form data when modal opens or card changes
    useEffect(() => {
        if (isOpen) {
            const initialOptions = initializeSelectedOptions();
            setSelectedOptions(initialOptions);
            
            // Store initial categories for tracking removals
            if (card?.categories && card.categories.length > 0) {
                initialCategoriesRef.current = card.categories.filter(cardCategory => 
                    categories.some((userCategory:any) => userCategory.id === cardCategory.id)
                ).map(cardCategory => cardCategory.id);
            }

            setFormData(prev => ({
                ...prev,
                name: card?.name || '',
                category_id: card?.category_id || categoryId || '',
                card_type: card?.card_type || '',
                encrypted_card_holder_name: card?.encrypted_card_holder_name || '',
                encrypted_card_number: card?.encrypted_card_number || '',
                encrypted_expiration_date: card?.encrypted_expiration_date || '',
                encrypted_cvv: card?.encrypted_cvv || '',
                encrypted_pin: card?.encrypted_pin || '',
                encrypted_postal_code: card?.encrypted_postal_code || '',
                encrypted_notes: card?.encrypted_notes || '',
                favourite: card?.favourite || false,
                shared_key: card?.shared_key || '',
                category_ids: initialOptions.map(opt => opt.value),
                remove_category_ids: []
            }));

            // if (!isSharedCard) {
            //     refreshCategories();
            // }
        }
    }, [isOpen, card, categories, categoryId]);

    // Reset when modal closes
    useEffect(() => {
        if (!isOpen) {
            initialCategoriesRef.current = [];
            setSelectedOptions([]);
            setFormData(prev => ({
                ...prev,
                category_ids: [],
                remove_category_ids: []
            }));
        }
    }, [isOpen]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        const isCheckbox = type === 'checkbox';
        const newValue = isCheckbox ? (e.target as HTMLInputElement).checked : value;

        // Special handling for category selection
        if (name === 'category_id' && value) {
            const category = categories.find(cat => cat.id === value);
            setSelectedCategory(category);
            
            // If this is a shared category, update the shared fields
            if (category?.is_shared) {
                setFormData(prev => ({
                    ...prev,
                    category_id: value,
                    isSharedUpdate: true,
                    shared_key: category.shared_key ? decryptKeyData(category.shared_key) : ''
                }));
            } else {
                setFormData(prev => ({
                    ...prev,
                    category_id: value,
                    isSharedUpdate: false,
                    shared_key: ''
                }));
            }
        } else {
            // For other fields
            setFormData(prev => ({
                ...prev,
                [name]: newValue
            }));
        }
    };

    // Handle multi-select change
    const handleMultiSelectChange = (selected: Option[]) => {
        const newSelectedIds = selected.map(opt => opt.value);
        
        // Compare with initial categories to determine which ones were removed
        const removedCategoryIds = initialCategoriesRef.current.filter(
            id => !newSelectedIds.includes(id)
        );

        console.log('Initial Categories:', initialCategoriesRef.current);
        console.log('New Selected IDs:', newSelectedIds);
        console.log('All Removed Category IDs:', removedCategoryIds);

        setSelectedOptions(selected);
        
        // Check if any selected category is shared
        const hasSharedCategory = selected.some(opt => opt.isShared);
        const sharedOption = selected.find(opt => opt.isShared);
        
        console.log("hasSharedCategory", hasSharedCategory);
        // Determine the shared key value, ensuring it's always a string
        const newSharedKey = hasSharedCategory && sharedOption?.sharedKey ? 
            decryptKeyData(sharedOption.sharedKey) : 
            (isSharedCard ? card?.shared_key || '' : '');

        setFormData(prev => ({
            ...prev,
            category_ids: newSelectedIds,
            remove_category_ids: removedCategoryIds,
            shared_key: newSharedKey
        }));
    };

    const handleExpirationDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        // Remove any non-digit characters
        const cleanedValue = value.replace(/\D/g, '');
        let formattedValue = '';

        if (cleanedValue.length > 0) {
            formattedValue += cleanedValue.substring(0, 2); // MM
        }
        if (cleanedValue.length > 2) {
            formattedValue += '/' + cleanedValue.substring(2, 4); // YY
        }

        setFormData(prev => ({
            ...prev,
            encrypted_expiration_date: formattedValue
        }));
    };

    const handleCreateCategory = async () => {
        if (!newCategoryName.trim()) return;

        setCreatingCategoryLoading(true);
        try {
            const newCategory = await createCategory({ name: newCategoryName, workspace_id: activeWorkspace?.id || undefined });
            await refreshCategories();

            // Find the newly created category and select it
            // const newCategory = categories.find(cat => cat.name === newCategoryName);
            // if (newCategory) {
            //     setFormData(prev => ({
            //         ...prev,
            //         category_id: newCategory.id
            //     }));
            // }

            const newOption: Option = {
                label: newCategoryName,
                value: newCategory.id,
                isShared: false,
                sharedKey: undefined
            };
            setSelectedOptions([...selectedOptions, newOption]);

            // Reset the new category UI state
            setNewCategoryName('');
            setIsCreatingCategory(false);
        } catch (error) {
            console.error('Error creating category:', error);
            toast.error('Failed to create category');
        } finally {
            setCreatingCategoryLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        const workspaceId = activeWorkspace?.id || team_id || undefined;

        // Validate expiration date
        const [month, year] = formData.encrypted_expiration_date.split('/').map(Number);
        if (month < 1 || month > 12) {
            toast.error('Invalid Expiration Date Month');
            setLoading(false);
            return;
        }
        if (year < 0 || year > 99) {
            toast.error('Year must be a valid two-digit year');
            setLoading(false);
            return;
        }

        try {
            if (card) {
                let isVaultOwner = false;
                console.log(card);
                if (card?.shared_key) {
                    try {
                        isVaultOwner = card.shared_key === secureStore.getVaultKey();
                    } catch (error) {
                    console.error('Error decrypting shared key:', error);
                    }
                }

                // Prepare the data to be sent
                const updatedData = {
                    id: card.id,
                    ...formData,
                    category_ids: selectedOptions.length > 0 ? selectedOptions.map(opt => opt.value) : [], // Set to [] if no options selected
                    remove_category_ids: formData.remove_category_ids,
                    favourite: formData.favourite,
                    isSharedUpdate: isVaultOwner ? isVaultOwner : isSharedCard,
                    shared_key: isVaultOwner ? card.shared_key : formData.shared_key || card.shared_key || sharedKey,
                    itemType: isVaultOwner || isSharedCard ? 'category' : card?.itemType || 'card',
                    workspace_id: workspaceId,
                };

                await CardDetailsService.updateCardDetail(card.id, updatedData);
                toast.success('Card updated successfully');
            } else {
                await CardDetailsService.createCardDetail({
                    ...formData,
                    category_ids: selectedOptions.map(opt => opt.value), // This will be [] if no options selected
                    favourite: formData.favourite,
                    isSharedUpdate: selectedCategory?.is_shared || false,
                    shared_key: secureStore.getVaultKey(),
                    workspace_id: workspaceId,
                });
                toast.success('Card created successfully');
            }

            if (onSuccessfulUpdate) {
                await onSuccessfulUpdate();
            } else {
                onSuccess();
            }
            onClose();
        } catch (error) {
            console.error('Error saving card:', error);
            toast.error(card ? 'Failed to update card' : 'Failed to create card');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    console.log(card);

    // const isReadOnly = !card?.is_owner && isSharedCard && permissionLevel === 'read' && role_id !== 1 && role_id !== 2;
    const isReadOnly = !card?.is_owner && isSharedCard && card?.permission_level === 'read' && !(role_id === 1 || role_id === 2);
    console.log("isReadOnly", isReadOnly);

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Enhanced Backdrop */}
            <div className="fixed inset-0 bg-gradient-to-br from-black/60 to-black/40 backdrop-blur-sm transition-all duration-300"></div>

            {/* Modal Container */}
            <div className="flex min-h-full items-center justify-center p-2 sm:p-4">
                <div className="relative bg-white rounded-2xl sm:rounded-3xl w-full max-w-7xl mx-2 sm:mx-4 max-h-[95vh] overflow-hidden shadow-2xl border border-gray-100">
                    {/* Modal Header */}
                    <div className="sticky top-0 z-10 bg-gradient-to-r from-primary-600 to-primary-700 text-white px-4 sm:px-8 py-4 sm:py-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2 sm:space-x-4">
                                <div className="p-2 sm:p-3 bg-white/20 rounded-xl sm:rounded-2xl backdrop-blur-sm">
                                    <CreditCardIcon className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-lg sm:text-2xl font-bold">
                                        {isReadOnly ? 'View Card Details' : (card ? 'Edit Card' : 'Add New Card')}
                                    </h3>
                                    <p className="text-primary-100 text-xs sm:text-sm hidden sm:block">
                                        {isReadOnly ? 'Review card information' : 'Manage your payment card details securely'}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 sm:p-3 text-white/80 hover:text-white hover:bg-white/20 rounded-xl sm:rounded-2xl transition-all duration-200"
                            >
                                <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                            </button>
                        </div>
                    </div>

                    {/* Modal Content */}
                    <form onSubmit={handleSubmit} className="flex flex-col h-full" autoComplete="off">
                        <div className="flex-1 overflow-auto max-h-[calc(95vh-120px)] sm:max-h-[calc(95vh-140px)] bg-gray-50">
                            <div className="p-4 sm:p-8">
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8">
                                    {/* Left Column - Basic Info & Vaults */}
                                    <div className="lg:col-span-1 order-1">
                                        <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-100 h-fit mb-4 lg:mb-0">
                                            <div className="flex items-center space-x-2 sm:space-x-3 mb-4 sm:mb-6">
                                                <div className="p-1.5 sm:p-2 bg-primary-100 rounded-lg sm:rounded-xl">
                                                    <DocumentTextIcon className="w-4 h-4 sm:w-5 sm:h-5 text-primary-600" />
                                                </div>
                                                <h4 className="text-base sm:text-lg font-semibold text-gray-900">Card Details</h4>
                                            </div>

                                            <div className="space-y-4 sm:space-y-6">
                                                {/* Card Name */}
                                                <div>
                                                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                                        Name on Card
                                                    </label>
                                                    <div className="relative">
                                                        <input
                                                            type="text"
                                                            name="name"
                                                            value={formData.name}
                                                            onChange={handleChange}
                                                            disabled={isReadOnly}
                                                            required
                                                            className={`w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ${
                                                                isReadOnly ? 'bg-gray-100 text-gray-600' : 'bg-white'
                                                            } border-gray-300`}
                                                            placeholder="Enter name on card"
                                                        />
                                                        <UserIcon className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                                                    </div>
                                                </div>

                                                {/* Vaults Section */}
                                                {isAdminView || (!isSharedCard || role_id === 1 || role_id === 2) && (
                                                    <div>
                                                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2 sm:mb-3">
                                                            <FolderIcon className="inline w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                                                            Vaults
                                                        </label>
                                                        <div className="space-y-2 sm:space-y-3">
                                                            <div className="text-sm">
                                                                <MultiSelect
                                                                    options={categoryOptions}
                                                                    value={selectedOptions}
                                                                    onChange={handleMultiSelectChange}
                                                                    labelledBy="Select vaults"
                                                                    disabled={isReadOnly}
                                                                    hasSelectAll={false}
                                                                    className="w-full"
                                                                    ClearSelectedIcon={null}
                                                                    debounceDuration={0}
                                                                />
                                                            </div>

                                                            {/* Create New Vault */}
                                                            {!isReadOnly && (
                                                                <div>
                                                                    {!isCreatingCategory ? (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setIsCreatingCategory(true)}
                                                                            className="w-full flex items-center justify-center px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg sm:rounded-xl transition-all duration-200"
                                                                        >
                                                                            <PlusIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                                                                            Create New Vault
                                                                        </button>
                                                                    ) : (
                                                                        <div className="space-y-2 sm:space-y-3 p-3 sm:p-4 bg-gray-50 rounded-lg sm:rounded-xl">
                                                                            <input
                                                                                type="text"
                                                                                value={newCategoryName}
                                                                                onChange={(e) => setNewCategoryName(e.target.value)}
                                                                                className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-sm border border-gray-300 rounded-md sm:rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                                                                placeholder="Enter vault name"
                                                                                autoFocus
                                                                            />
                                                                            <div className="flex space-x-2">
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={handleCreateCategory}
                                                                                    disabled={creatingCategoryLoading || !newCategoryName.trim()}
                                                                                    className={`flex-1 py-1.5 sm:py-2 rounded-md sm:rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
                                                                                        creatingCategoryLoading || !newCategoryName.trim()
                                                                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                                                        : 'bg-primary-600 text-white hover:bg-primary-700'
                                                                                    }`}
                                                                                >
                                                                                    {creatingCategoryLoading ? 'Creating...' : 'Create'}
                                                                                </button>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => {
                                                                                        setIsCreatingCategory(false);
                                                                                        setNewCategoryName('');
                                                                                    }}
                                                                                    className="flex-1 py-1.5 sm:py-2 rounded-md sm:rounded-lg text-xs sm:text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 transition-all duration-200"
                                                                                >
                                                                                    Cancel
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Favorite Toggle */}
                                                {!isAdminView && (
                                                <div className="flex items-center p-3 sm:p-4 bg-gradient-to-r from-red-50 to-pink-50 rounded-lg sm:rounded-xl border border-red-100">
                                                    <input
                                                        type="checkbox"
                                                        id="favourite"
                                                        name="favourite"
                                                        checked={formData.favourite}
                                                        onChange={(e) => setFormData({ ...formData, favourite: e.target.checked })}
                                                        className="h-4 w-4 sm:h-5 sm:w-5 text-red-500 focus:ring-red-400 border-red-300 rounded"
                                                        disabled={isReadOnly}
                                                    />
                                                    <label htmlFor="favourite" className="ml-2 sm:ml-3 flex items-center text-xs sm:text-sm font-medium text-red-700">
                                                        {formData.favourite ? (
                                                            <HeartSolidIcon className="w-4 h-4 sm:w-5 sm:h-5 text-red-500 mr-1 sm:mr-2" />
                                                        ) : (
                                                            <HeartIcon className="w-4 h-4 sm:w-5 sm:h-5 text-red-400 mr-1 sm:mr-2" />
                                                        )}
                                                        Add to favorites
                                                    </label>
                                                </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Middle Column - Card Information */}
                                    <div className="lg:col-span-1 order-2">
                                        <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-100 h-fit mb-4 lg:mb-0">
                                            <div className="flex items-center space-x-2 sm:space-x-3 mb-4 sm:mb-6">
                                                <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg sm:rounded-xl">
                                                    <CreditCardIcon className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                                                </div>
                                                <h4 className="text-base sm:text-lg font-semibold text-gray-900">Card Information</h4>
                                            </div>

                                            <div className="space-y-4 sm:space-y-6">
                                                {/* Card Type */}
                                                <div>
                                                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                                        Card Type
                                                    </label>
                                                    <div className="relative">
                                                        <input
                                                            type="text"
                                                            name="card_type"
                                                            value={formData.card_type}
                                                            onChange={handleChange}
                                                            disabled={isReadOnly}
                                                            className={`w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                                                                isReadOnly ? 'bg-gray-100 text-gray-600' : 'bg-white'
                                                            } border-gray-300`}
                                                            placeholder="e.g., Visa, MasterCard, AmEx"
                                                        />
                                                        <CreditCardIcon className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                                                    </div>
                                                </div>

                                                {/* Card Holder Name */}
                                                <div>
                                                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                                        Card Holder Name
                                                    </label>
                                                    <div className="relative">
                                                        <input
                                                            type="text"
                                                            name="encrypted_card_holder_name"
                                                            value={formData.encrypted_card_holder_name}
                                                            onChange={handleChange}
                                                            disabled={isReadOnly}
                                                            required
                                                            className={`w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                                                                isReadOnly ? 'bg-gray-100 text-gray-600' : 'bg-white'
                                                            } border-gray-300`}
                                                            placeholder="Full name on card"
                                                        />
                                                        <UserIcon className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                                                    </div>
                                                </div>

                                                {/* Card Number */}
                                                <div>
                                                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                                        Card Number
                                                    </label>
                                                    <div className="relative">
                                                        <input
                                                            type="text"
                                                            name="encrypted_card_number"
                                                            value={formData.encrypted_card_number}
                                                            onChange={handleChange}
                                                            disabled={isReadOnly}
                                                            required
                                                            className={`w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                                                                isReadOnly ? 'bg-gray-100 text-gray-600' : 'bg-white'
                                                            } border-gray-300`}
                                                            placeholder="1234 5678 9012 3456"
                                                        />
                                                        <CreditCardIcon className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                                                    </div>
                                                </div>

                                                {/* Expiration Date and CVV */}
                                                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                                                    <div>
                                                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                                            Expiration Date
                                                        </label>
                                                        <div className="relative">
                                                            <input
                                                                type="text"
                                                                name="encrypted_expiration_date"
                                                                value={formData.encrypted_expiration_date}
                                                                onChange={handleExpirationDateChange}
                                                                disabled={isReadOnly}
                                                                required
                                                                placeholder="MM/YY"
                                                                className={`w-full pl-7 sm:pl-10 pr-2 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 ${
                                                                    isReadOnly ? 'bg-gray-100 text-gray-600' : 'bg-white'
                                                                } border-gray-300`}
                                                            />
                                                            <CalendarIcon className="absolute left-1.5 sm:left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                                            CVV
                                                        </label>
                                                        <div className="relative">
                                                            <input
                                                                type={showCvv ? 'text' : 'password'}
                                                                name="encrypted_cvv"
                                                                value={formData.encrypted_cvv}
                                                                onChange={handleChange}
                                                                disabled={isReadOnly}
                                                                maxLength={5}
                                                                className={`w-full pl-7 sm:pl-10 pr-10 sm:pr-12 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 ${
                                                                    isReadOnly ? 'bg-gray-100 text-gray-600' : 'bg-white'
                                                                } border-gray-300`}
                                                                placeholder="123"
                                                            />
                                                            <KeyIcon className="absolute left-1.5 sm:left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                                                            <button
                                                                type="button"
                                                                onClick={() => setShowCvv(prev => !prev)}
                                                                className="absolute inset-y-0 right-2 sm:right-3 flex items-center px-1 text-gray-500 hover:text-gray-700"
                                                                aria-label={showCvv ? 'Hide CVV' : 'Show CVV'}
                                                            >
                                                                {showCvv ? (
                                                                    <EyeSlashIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                                                                ) : (
                                                                    <EyeIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Card PIN */}
                                                <div>
                                                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                                        Card PIN
                                                    </label>
                                                    <div className="relative">
                                                        <input
                                                            type={showCardPin ? 'text' : 'password'}
                                                            name="encrypted_pin"
                                                            value={formData.encrypted_pin}
                                                            onChange={handleChange}
                                                            disabled={isReadOnly}
                                                            className={`w-full pl-8 sm:pl-10 pr-10 sm:pr-12 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 ${
                                                                isReadOnly ? 'bg-gray-100 text-gray-600' : 'bg-white'
                                                            } border-gray-300`}
                                                            placeholder="Enter PIN"
                                                        />
                                                        <KeyIcon className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowCardPin(prev => !prev)}
                                                            className="absolute inset-y-0 right-2 sm:right-3 flex items-center px-1 text-gray-500 hover:text-gray-700"
                                                            aria-label={showCardPin ? 'Hide card PIN' : 'Show card PIN'}
                                                        >
                                                            {showCardPin ? (
                                                                <EyeSlashIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                                                            ) : (
                                                                <EyeIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                                                            )}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right Column - Additional Information */}
                                    <div className="lg:col-span-1 order-3">
                                        <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-100 h-fit">
                                            <div className="flex items-center space-x-2 sm:space-x-3 mb-4 sm:mb-6">
                                                <div className="p-1.5 sm:p-2 bg-green-100 rounded-lg sm:rounded-xl">
                                                    <DocumentTextIcon className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                                                </div>
                                                <h4 className="text-base sm:text-lg font-semibold text-gray-900">Additional Info</h4>
                                            </div>

                                            <div className="space-y-4 sm:space-y-6">
                                                {/* Postal Code */}
                                                <div>
                                                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                                        Postal Code
                                                    </label>
                                                    <div className="relative">
                                                        <input
                                                            type="text"
                                                            name="encrypted_postal_code"
                                                            value={formData.encrypted_postal_code}
                                                            onChange={handleChange}
                                                            disabled={isReadOnly}
                                                            className={`w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 ${
                                                                isReadOnly ? 'bg-gray-100 text-gray-600' : 'bg-white'
                                                            } border-gray-300`}
                                                            placeholder="Enter postal code"
                                                        />
                                                        <MapPinIcon className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                                                    </div>
                                                </div>

                                                {/* Notes */}
                                                <div>
                                                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                                        Notes
                                                    </label>
                                                    <textarea
                                                        name="encrypted_notes"
                                                        value={formData.encrypted_notes}
                                                        onChange={handleChange}
                                                        rows={6}
                                                        disabled={isReadOnly}
                                                        className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 resize-none ${
                                                            isReadOnly ? 'bg-gray-100 text-gray-600' : 'bg-white'
                                                        }`}
                                                        placeholder="Add any additional notes about this card..."
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Enhanced Footer */}
                        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 sm:px-8 py-4 sm:py-6">
                            <div className="flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0">
                                <div className="flex items-center space-x-2 text-xs sm:text-sm text-gray-500 order-2 sm:order-1">
                                    <ShieldCheckIcon className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
                                    <span>All card data is encrypted and secure</span>
                                </div>
                                <div className="flex space-x-3 sm:space-x-4 w-full sm:w-auto order-1 sm:order-2">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="flex-1 sm:flex-none px-4 sm:px-6 py-2 sm:py-3 border border-gray-300 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 transition-all duration-200"
                                    >
                                        {isReadOnly ? 'Close' : 'Cancel'}
                                    </button>

                                    {isAdminView || !isReadOnly && (
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className={`flex-1 sm:flex-none px-6 sm:px-8 py-2 sm:py-3 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium text-white transition-all duration-200 ${
                                                loading
                                                    ? 'bg-gray-400 cursor-not-allowed'
                                                    : 'bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 shadow-lg hover:shadow-xl'
                                            }`}
                                        >
                                            {loading ? (
                                                <div className="flex items-center justify-center">
                                                    <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-white mr-2"></div>
                                                    Saving...
                                                </div>
                                            ) : (
                                                card ? 'Update Card' : 'Save Card'
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CardDetailsModal; 